<template>
  <v-card elevation="0" outlined class="mb-4">
    <v-card-title
      class="subtitle-1 font-weight-medium"
      style="cursor: pointer"
      @click="collapsed = !collapsed"
    >
      <v-icon class="mr-2" color="primary">mdi-folder-sync-outline</v-icon>
      {{ mapping.mapping_name }}
      <a
        v-if="mapping.drive_folder_id"
        :href="mapping.drive_folder_url"
        target="_blank"
        rel="noopener"
        class="ml-3 d-inline-flex align-center text-decoration-none caption"
        style="color: inherit"
        @click.stop
      >
        <v-icon x-small class="mr-1" color="blue darken-1">mdi-google-drive</v-icon>
        <span class="blue--text text--darken-1">Drive</span>
      </a>
      <a
        v-if="mapping.vector_store_id"
        :href="mapping.vector_store_url"
        target="_blank"
        rel="noopener"
        class="ml-2 d-inline-flex align-center text-decoration-none caption"
        style="color: inherit"
        @click.stop
      >
        <open-ai-logo :size="14" class="mr-1" />
        <span>OpenAI</span>
      </a>
      <v-spacer />
      <span class="caption grey--text mr-3" v-if="mapping.last_synced_at">
        Dernière synchro : {{ formatDate(mapping.last_synced_at) }}
      </span>
      <span class="caption grey--text mr-3" v-else>
        Jamais synchronisé
      </span>
      <v-btn
        x-small
        elevation="0"
        :loading="previewing"
        class="mr-1"
        @click.stop="$emit('preview', mapping.mapping_id)"
      >
        <v-icon x-small left>mdi-eye-outline</v-icon>
        <span class="text-none">Prévisualiser</span>
      </v-btn>
      <v-btn
        x-small
        color="primary"
        elevation="0"
        :loading="syncing"
        :disabled="!hasPendingChanges || running"
        class="mr-2"
        @click.stop="$emit('sync', mapping.mapping_id)"
      >
        <v-icon x-small left>mdi-sync</v-icon>
        <span class="text-none">Synchroniser</span>
      </v-btn>
      <v-icon :style="{ transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }">
        mdi-chevron-down
      </v-icon>
    </v-card-title>

    <v-card-text v-show="!collapsed" class="pb-1">

      <!-- Error -->
      <v-alert v-if="mapping.error" type="error" dense outlined class="mb-2">
        {{ mapping.error }}
      </v-alert>

      <!-- Nothing to do -->
      <v-alert
        v-else-if="mapping.to_add.length === 0 && mapping.to_remove.length === 0"
        type="success" dense outlined class="mb-2"
      >
        À jour — {{ mapping.in_sync.length }} fichier(s) synchronisé(s)
      </v-alert>

      <!-- To remove (vector-store-only files, not in Drive) -->
      <div v-if="mapping.to_remove.length > 0" class="mb-2">
        <div class="caption font-weight-medium error--text mb-1">
          <v-icon small color="error">mdi-minus-circle-outline</v-icon>
          À supprimer ({{ mapping.to_remove.length }})
        </div>
        <file-chip
          v-for="f in mapping.to_remove"
          :key="f.file_id"
          :file="f"
          color="error"
        />
      </div>

      <!-- Drive folder tree -->
      <div v-if="!mapping.error && mapping.folder_tree">
        <div class="caption font-weight-medium grey--text text--darken-1 mb-1">
          <v-icon small>mdi-folder-outline</v-icon>
          Structure Google Drive
          <span v-if="mapping.to_add.length > 0" class="success--text ml-1">
            — {{ mapping.to_add.length }} à ajouter
          </span>
        </div>
        <v-treeview
          :items="mapping.folder_tree"
          dense
          open-on-click
        >
          <template #prepend="{ item }">
            <v-icon small :color="treeNodeColor(item)">
              {{ item.type === 'folder' ? 'mdi-folder-outline' : 'mdi-file-outline' }}
            </v-icon>
          </template>
          <template #label="{ item }">
            <component
              :is="item.type === 'file' && item.url ? 'a' : 'span'"
              :href="item.type === 'file' ? item.url : undefined"
              :target="item.type === 'file' && item.url ? '_blank' : undefined"
              :rel="item.type === 'file' && item.url ? 'noopener' : undefined"
              :class="treeNodeClass(item)"
              class="caption text-decoration-none"
              style="color: inherit"
            >
              {{ item.name }}
              <span
                v-if="item.type === 'folder' && countChanges(item) > 0"
                class="success--text font-weight-medium ml-1"
              >+{{ countChanges(item) }}</span>
            </component>
          </template>
        </v-treeview>
      </div>

    </v-card-text>
  </v-card>
</template>

<script>
import FileChip from '@/components/ui/FileChip.vue'
import OpenAiLogo from '@/components/ui/OpenAiLogo.vue'

export default {
  name: 'SyncMappingCard',
  components: { FileChip, OpenAiLogo },

  props: {
    mapping: { type: Object, required: true },
    running:  { type: Boolean, default: false },
    previewing: { type: Boolean, default: false },
    syncing:    { type: Boolean, default: false },
  },

  emits: ['preview', 'sync'],

  data() {
    return {
      collapsed: false,
    }
  },

  computed: {
    hasPendingChanges() {
      return this.mapping.to_add.length > 0 || this.mapping.to_remove.length > 0
    },
  },

  methods: {
    countChanges(node) {
      if (node.type === 'file') return node.status === 'to_add' ? 1 : 0
      if (!node.children) return 0
      return node.children.reduce((sum, child) => sum + this.countChanges(child), 0)
    },

    formatDate(iso) {
      return new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })
    },

    treeNodeColor(item) {
      if (item.type === 'folder') return 'grey'
      return item.status === 'to_add' ? 'success' : 'grey'
    },

    treeNodeClass(item) {
      if (item.type === 'file' && item.status === 'to_add') return 'success--text font-weight-medium'
      return 'grey--text text--darken-2'
    },
  },
}
</script>
